import { useState, useEffect, useCallback } from "react";
import { 
  getFarmers, 
  addMilkEntry, 
  getMilkEntries, 
  updateMilkEntry, 
  deleteMilkEntry,
  getCattleFoodEntries,
  getAmountGiven,
  getFatWisePrices
} from "../services/firebaseService";

function getToday() {
  return new Date().toISOString().slice(0, 10);
}

export default function MilkEntry() {
  const [farmers, setFarmers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [cattleFoodEntries, setCattleFoodEntries] = useState([]);
  const [fatWisePrices, setFatWisePrices] = useState({});
  const [amountGivenCache, setAmountGivenCache] = useState({});
  const [date, setDate] = useState(getToday());
  const [form, setForm] = useState({
    farmerCode: "",
    animalType: "Cow",
    time: "Morning",
    quantity: "",
    fat: "",
    price: "",
  });
  const [editIndex, setEditIndex] = useState(null);
  const [editEntryId, setEditEntryId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const farmersData = await getFarmers();
      setFarmers(farmersData);
      const entriesData = await getMilkEntries();
      setEntries(entriesData);
      const cattleFoodData = await getCattleFoodEntries();
      setCattleFoodEntries(cattleFoodData);
      
      // Load fat-wise prices for both cow and buffalo
      const cowPrices = await getFatWisePrices('cow');
      const buffPrices = await getFatWisePrices('buffalo');
      setFatWisePrices({
        cow: cowPrices,
        buffalo: buffPrices
      });
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [loadData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Auto-calculate price based on fat if fat-wise prices exist
    if (name === 'fat' && form.animalType) {
      const animalKey = form.animalType.toLowerCase();
      const prices = fatWisePrices[animalKey] || [];
      const fat = parseFloat(value);
      
      if (fat && prices.length > 0) {
        // Find closest fat percentage
        let closestPrice = prices[0];
        let minDiff = Math.abs(fat - parseFloat(closestPrice.fat));
        
        for (let p of prices) {
          const diff = Math.abs(fat - parseFloat(p.fat));
          if (diff < minDiff) {
            minDiff = diff;
            closestPrice = p;
          }
        }
        
        setForm(prev => ({ ...prev, price: closestPrice.amount.toFixed(2) }));
      }
    }
  };
  const handleDateChange = (e) => {
    setDate(e.target.value);
    setEditIndex(null);
    setEditEntryId(null);
    setForm({ farmerCode: "", animalType: "Cow", time: "Morning", quantity: "", fat: "", price: "" });
  };

  const handleSubmit = async (e, shouldPrint = false) => {
    e.preventDefault();
    if (!form.farmerCode || !form.animalType || !form.time || !form.quantity || !form.fat || !form.price) return;
    
    try {
      setSubmitting(true);
      let savedEntry;
      if (editIndex === null) {
        const newEntry = { date, farmerCode: form.farmerCode, animalType: form.animalType, time: form.time, quantity: Number(form.quantity), fat: Number(form.fat), price: Number(form.price) };
        savedEntry = await addMilkEntry(newEntry);
        setEntries([savedEntry, ...entries]);
      } else {
        const updatedEntry = { date, farmerCode: form.farmerCode, animalType: form.animalType, time: form.time, quantity: Number(form.quantity), fat: Number(form.fat), price: Number(form.price) };
        await updateMilkEntry(editEntryId, updatedEntry);
        setEntries(entries.map(entry => entry.id === editEntryId ? { ...entry, ...updatedEntry } : entry));
        setEditIndex(null);
        setEditEntryId(null);
      }
      
      if (shouldPrint) {
        const farmer = farmers.find(f => f.code === form.farmerCode);
        if (farmer) {
          handlePrintMilkTicket(farmer);
        }
      }
      
      setForm({ farmerCode: "", animalType: "Cow", time: "Morning", quantity: "", fat: "", price: "" });
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Error saving entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (idx) => {
    const entry = todaysEntries[idx];
    setForm({ farmerCode: entry.farmerCode, animalType: entry.animalType, time: entry.time, quantity: entry.quantity, fat: entry.fat, price: entry.price });
    setEditIndex(idx);
    setEditEntryId(entry.id);
  };

  const handleCancel = () => {
    setEditIndex(null);
    setEditEntryId(null);
    setForm({ farmerCode: "", animalType: "Cow", time: "Morning", quantity: "", fat: "", price: "" });
  };

  const handleDelete = async (idx) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    try {
      const entry = todaysEntries[idx];
      await deleteMilkEntry(entry.id);
      setEntries(entries.filter(e => e.id !== entry.id));
      if (editIndex === idx) handleCancel();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry. Please try again.');
    }
  };

  const todaysEntries = entries.filter((e) => e.date === date);
  const totalQuantity = todaysEntries.reduce((sum, e) => sum + e.quantity, 0);
  const totalAmount = todaysEntries.reduce((sum, e) => sum + (e.quantity * e.price), 0);

  const handlePrintMilkTicket = (farmer) => {
    const farmerEntries = todaysEntries.filter(e => e.farmerCode === farmer.code);
    const farmerCattleFoodEntries = cattleFoodEntries.filter(e => e.farmerCode === farmer.code && e.date === date);
    
    const sumAmt = (animal, time) => farmerEntries.filter(e => e.animalType === animal && e.time === time).reduce((s, e) => s + (e.quantity * e.price || 0), 0);
    const cowMorningAmt = sumAmt("Cow", "Morning");
    const cowEveningAmt = sumAmt("Cow", "Evening");
    const buffaloMorningAmt = sumAmt("Buffalo", "Morning");
    const buffaloEveningAmt = sumAmt("Buffalo", "Evening");
    const totalMilkAmt = cowMorningAmt + cowEveningAmt + buffaloMorningAmt + buffaloEveningAmt;
    const cattleFoodTotal = farmerCattleFoodEntries.reduce((sum, entry) => sum + entry.totalAmount, 0);
    const remaining = totalMilkAmt - cattleFoodTotal;

    const ticketHTML = `
      <html>
        <head>
          <title>Milk Bill - ${farmer.name}</title>
          <style>
            body { font-family: 'Courier New', monospace; margin: 0; padding: 10px; background: white; }
            .ticket { width: 300px; margin: 0 auto; border: 2px dashed #333; padding: 15px; background: white; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 10px; }
            .header h2 { margin: 0; font-size: 18px; }
            .header p { margin: 5px 0; font-size: 12px; }
            .section { margin: 10px 0; }
            .section-title { font-weight: bold; border-bottom: 1px solid #333; padding-bottom: 5px; margin-bottom: 5px; }
            .row { display: flex; justify-content: space-between; font-size: 12px; padding: 3px 0; }
            .label { flex: 1; }
            .value { text-align: right; font-weight: bold; }
            .total-row { border-top: 2px solid #333; border-bottom: 2px solid #333; padding: 5px 0; margin: 10px 0; }
            .footer { text-align: center; font-size: 11px; margin-top: 15px; border-top: 2px solid #333; padding-top: 10px; }
            @media print { body { margin: 0; padding: 0; } .ticket { border: none; width: 100%; } }
          </style>
        </head>
        <body>
          <div class="ticket">
            <div class="header">
              <h2>🐄 DAIRY MILK BILL</h2>
              <p>Date: ${date}</p>
            </div>

            <div class="section">
              <div class="section-title">FARMER DETAILS</div>
              <div class="row"><span class="label">Name:</span><span class="value">${farmer.name}</span></div>
              <div class="row"><span class="label">Code:</span><span class="value">${farmer.code}</span></div>
              <div class="row"><span class="label">Mobile:</span><span class="value">${farmer.mobile}</span></div>
            </div>

            <div class="section">
              <div class="section-title">MILK COLLECTION (Fat-wise)</div>
              <div class="row"><span class="label">🐄 Cow Morning:</span><span class="value">₹${cowMorningAmt.toFixed(0)}</span></div>
              <div class="row"><span class="label">🐄 Cow Evening:</span><span class="value">₹${cowEveningAmt.toFixed(0)}</span></div>
              <div class="row"><span class="label">🦬 Buffalo Morning:</span><span class="value">₹${buffaloMorningAmt.toFixed(0)}</span></div>
              <div class="row"><span class="label">🦬 Buffalo Evening:</span><span class="value">₹${buffaloEveningAmt.toFixed(0)}</span></div>
            </div>

            <div class="section">
              <div class="section-title">DEDUCTIONS</div>
              <div class="row"><span class="label">🌾 Cattle Food:</span><span class="value">₹${cattleFoodTotal.toFixed(0)}</span></div>
            </div>

            <div class="section">
              <div class="row total-row">
                <span class="label">Total Milk Amount:</span>
                <span class="value">₹${totalMilkAmt.toFixed(0)}</span>
              </div>
              <div class="row"><span class="label">- Cattle Food:</span><span class="value">₹${cattleFoodTotal.toFixed(0)}</span></div>
              <div class="row total-row">
                <span class="label">REMAINING:</span>
                <span class="value" style="color: ${remaining < 0 ? 'red' : 'green'}">₹${remaining.toFixed(0)}</span>
              </div>
            </div>

            <div class="footer">
              <p>Generated: ${new Date().toLocaleString('en-IN')}</p>
              <p>Thank you for your business!</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '', 'height=600,width=400');
    if (printWindow) {
      printWindow.document.write(ticketHTML);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="container fade-in">
      <div className="card">
        <div className="form-header">
          <h2 style={{marginBottom: 20}}>{editIndex === null ? 'Add Milk Entry' : 'Edit Entry'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="form-container" style={{marginBottom: 24}}>
          <div className="form-row">
            <input type="date" value={date} onChange={handleDateChange} className="form--input" style={{maxWidth: isMobile ? '100%' : 180}} />
            <select name="farmerCode" value={form.farmerCode} onChange={handleChange} className="form--input" required>
              <option value="">Select Farmer</option>
              {farmers.map((f) => (<option key={f.id} value={f.code}>{f.name} ({f.code})</option>))}
            </select>
          </div>
          <div className="form-row">
            <select name="animalType" value={form.animalType} onChange={handleChange} className="form--input" required>
              <option value="Cow">Cow</option>
              <option value="Buffalo">Buffalo</option>
            </select>
            <select name="time" value={form.time} onChange={handleChange} className="form--input" required>
              <option value="Morning">Morning</option>
              <option value="Evening">Evening</option>
            </select>
          </div>
          <div className="form-row">
            <input type="number" name="quantity" placeholder="Milk Quantity (liters)" value={form.quantity} onChange={handleChange} className="form--input" min="0" step="0.1" required />
            <input type="number" name="fat" placeholder="Fat %" value={form.fat} onChange={handleChange} className="form--input" min="0" step="0.1" required />
          </div>
          <div className="form-row">
            <input type="number" name="price" placeholder="Milk Price (₹/L)" value={form.price} onChange={handleChange} className="form--input" min="0" step="0.01" required />
          </div>
          <div className="form-actions">
            {editIndex === null ? (
              <>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Entry'}</button>
                <button type="button" className="btn btn-primary" onClick={(e) => handleSubmit(e, true)} disabled={submitting}>{submitting ? 'Saving...' : 'Print and Save'}</button>
              </>
            ) : (
              <>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Updating...' : 'Update Entry'}</button>
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
              </>
            )}
          </div>
        </form>
      </div>

      <div className="summary-grid">
        <div className="summary-card"><span>📅 Date:</span><b>{new Date(date).toLocaleDateString('en-IN')}</b></div>
        <div className="summary-card"><span>🥛 Total Quantity:</span><b>{totalQuantity.toFixed(1)} L</b></div>
        <div className="summary-card"><span>💰 Total Amount:</span><b>₹{totalAmount.toFixed(2)}</b></div>
        <div className="summary-card"><span>📝 Entries:</span><b>{todaysEntries.length}</b></div>
      </div>

      <div className="card">
        <div className="table-header"><h3 style={{marginBottom: 16}}>Entries for {new Date(date).toLocaleDateString('en-IN')}</h3></div>
        <div className="table-container">
          <table className="table">
            <thead><tr><th>Farmer</th><th>Animal</th><th>Time</th><th>Quantity (L)</th><th>Fat %</th><th>Price (₹/L)</th><th>Total (₹)</th><th>Actions</th></tr></thead>
            <tbody>
              {loading ? (<tr><td colSpan="8" className="table-loading"><div className="mini-spinner"></div>Loading entries...</td></tr>
              ) : todaysEntries.length === 0 ? (<tr><td colSpan="8" className="empty-state">No entries for this date.</td></tr>
              ) : (todaysEntries.map((e, i) => {
                const farmer = farmers.find(f => f.code === e.farmerCode);
                return (
                  <tr key={`entry-${e.id}-${date}-${i}`}>
                    <td>{farmer ? `${farmer.name} (${farmer.code})` : e.farmerCode}</td>
                    <td>{e.animalType}</td><td>{e.time}</td><td>{e.quantity}</td><td>{e.fat?.toFixed(1) || '-'}</td><td>{e.price}</td><td>{(e.quantity * e.price).toFixed(2)}</td>
                    <td className="action-buttons">
                      <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(i)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(i)}>Delete</button>
                      <button className="btn btn-primary btn-sm" onClick={() => handlePrintMilkTicket(farmer)}>🖨️ Print</button>
                    </td>
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
